import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import './PackageDetailsPopup.css';

const PackageDetailsPopup = ({
  show,
  onClose,
  orderData = null,
  onConfirm,
  onReport,
  onShowDetails
}) => {
  const [selectedUnit, setSelectedUnit] = useState('cm');
  const [confirmAllPacked, setConfirmAllPacked] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportMessage, setReportMessage] = useState('');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  if (!orderData) return null;
  const {
    orderId = 'N/A',
    products = [],
    shippingAddress = {},
    orderPlaceDate,
    statusUpdateDate,
    _id,
    orderMainId
  } = orderData;

  const needsCheckbox = products.some(product => (product.quantity || 0) > 0);
  
  const getTotalPackages = () => {
    return products.reduce((total, product) => {
      const tracks = product.tracks || [];
      const packagesPerUnit = tracks.length;
      return total + packagesPerUnit;
    }, 0);
  };
  
  const totalPackages = getTotalPackages();
  const shouldShowPackedCheckbox = totalPackages > 1;

  const formatDate = (dateObj) => {
    if (!dateObj?.$date) return 'N/A';
    const date = new Date(dateObj.$date);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatNumber = (value, unit = '', isVolumetric = false) => {
    if (!value || isNaN(value)) return `0${unit ? ` ${unit}` : ''}`;
    const numValue = parseFloat(value);
    
    if (isVolumetric && numValue < 1) {
      const formatted = numValue.toFixed(6).replace(/\.?0+$/, '');
      return `${formatted}${unit ? ` ${unit}` : ''}`;
    }
    
    const formatted = parseFloat(numValue.toFixed(2)).toString();
    return `${formatted}${unit ? ` ${unit}` : ''}`;
  };

  const convertDimension = (value) => {
    if (!value || isNaN(value)) return 'N/A';
    const numValue = parseFloat(value);
    
    switch (selectedUnit) {
      case 'ft':
        return (numValue / 30.48).toFixed(2);
      case 'cm':
      default:
        return numValue.toFixed(1);
    }
  };

  const formatDimensions = (pkg) => {
    const formatDim = (value) => {
      if (!value || isNaN(value)) return '0';
      return parseFloat(value).toFixed(1).replace(/\.0$/, '');
    };

    if (pkg.actLength && pkg.actWidth && pkg.actHeight) {
      return `${formatDim(convertDimension(pkg.actLength))} × ${formatDim(convertDimension(pkg.actWidth))} × ${formatDim(convertDimension(pkg.actHeight))}`;
    }
    
    if (pkg.packageLength && pkg.packageWidth && pkg.packageHeight) {
      return `${formatDim(convertDimension(pkg.packageLength))} × ${formatDim(convertDimension(pkg.packageWidth))} × ${formatDim(convertDimension(pkg.packageHeight))}`;
    }

    return 'N/A';
  };

  const getNumericWeight = (weightValue) => {
    if (!weightValue) return 0;
    if (typeof weightValue === 'number') return weightValue;
    if (typeof weightValue === 'string') {
      const match = weightValue.toString().match(/(\d+(?:\.\d+)?)/);
      return match ? parseFloat(match[1]) : 0;
    }
    return 0;
  };

  const formatPrice = (value) => {
    if (!value || isNaN(value)) return '₹0';
    return `₹${parseFloat(value).toFixed(0)}`;
  };

  const handleModalClose = () => {
    setConfirmAllPacked(false);
    setShowReportForm(false);
    setReportMessage('');
    setShowSuccessMessage(false);
    if (onClose && typeof onClose === 'function') {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (!shouldShowPackedCheckbox || confirmAllPacked) {
      if (onConfirm && typeof onConfirm === 'function') {
        onConfirm(orderData);
      }
      handleModalClose();
    }
  };

  const handleCancel = () => {
    try {
      setShowReportForm(true);
      setShowSuccessMessage(false);
    } catch (error) {
      console.error('Error in handleCancel:', error);
    }
  };

  const handleReportSubmit = async () => {
    try {
      setIsSubmittingReport(true);
      
      const getVendorIdAndSku = () => {
        let vendorId = null;
        let sku = null;
        
        if (products && Array.isArray(products) && products.length > 0) {
          const firstProduct = products[0];
          const productData = firstProduct.productId || {};
          sku = productData.SKU;
          
          if (productData.vendorId) {
            vendorId = productData.vendorId;
          }
        }
        
        if (!vendorId && orderData.vendorId) {
          vendorId = orderData.vendorId;
        }
        
        return { vendorId, sku };
      };
      
      const { vendorId, sku } = getVendorIdAndSku();
      
      const payload = {
        _id: _id || orderMainId,
        orderId: orderId,
        message: reportMessage.trim(),
        reportType: "WRONG_PRODUCT_SHIPPED",
        timestamp: new Date().toISOString(),
        vendorId: localStorage.getItem('vendorId'),
        SKU: sku
      };

      if (onReport && typeof onReport === 'function') {
        await onReport(payload);
      }

      setShowSuccessMessage(true);
      setShowReportForm(false);
      
      setTimeout(() => {
        handleModalClose();
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting report:', error);
      alert('Failed to submit report. Please try again.');
    } finally {
      setIsSubmittingReport(false);
    }
  };

  const handleReportCancel = () => {
    setShowReportForm(false);
    setReportMessage('');
  };

  const handleDetailsClick = (productUrl) => {
    try {
      if (onShowDetails && typeof onShowDetails === 'function') {
        onShowDetails(productUrl);
      } else {
        console.warn('⚠️ onShowDetails prop not provided');
      }
    } catch (error) {
      console.error('❌ Error in handleDetailsClick:', error);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      backdrop="static"
      centered
      dialogClassName="package-dialog"
    >
      <Modal.Body className="package-body">
        <div className="package-header">
          <h5 className="package-title">
            {showSuccessMessage 
              ? "Report Submitted Successfully" 
              : showReportForm 
                ? "Report Wrong Product" 
                : "Confirm Product Details"}
          </h5>
          <button className="package-close" onClick={handleModalClose}>×</button>
        </div>

        <div className="package-scroll-wrapper">
          {showSuccessMessage ? (
            <div className="package-success-section">
              <div className="package-success-icon">✅</div>
              <div className="package-success-message">
                <h4>Report Submitted Successfully!</h4>
                <p>
                  Your report has been submitted to our team. We will review it and take appropriate action.
                  <br />
                  Thank you for helping us maintain quality standards.
                </p>
              </div>
            </div>
          ) : !showReportForm ? (
            <>
              {/* Single Warning Section */}
              <div className="package-warning-section">
                <div className="package-important-warning">
                  <div className="package-warning-icon">⚠️</div>
                  <div className="package-warning-content">
                    <div className="package-warning-text">
                      Shipping the wrong product may lead to penalties and account suspension. Please verify carefully before confirming.
                    </div>
                  </div>
                </div>
              </div>

              {products.map((product, productIndex) => {
                const productData = product.productId || {};
                const tracks = product.tracks || [];
                console.log(productData)
                return (
                  <div key={productIndex} className="product-section">
                    <div className="package-details">
                      <div className="package-image">
                        {productData.images && productData.images[0]?.image ? (
                          <img 
                            src={`https://s3.toolsvilla.com/${productData.images[0].image}`}
                            alt={productData.productName || 'Product'}
                            className="image"
                          />
                        ) : (
                          <div className="image-placeholder">📦</div>
                        )}
                      </div>
                      <div className="package-info">
                        <h6 className="info-title">
                          {productData.productName || 'Unknown Product'}
                          {productData.url && (
                            <button 
                              className="details-button"
                              onClick={() => handleDetailsClick(productData.url)}
                            >
                              Details
                            </button>
                          )}
                        </h6>

                        {productData.attributes &&
                          Array.isArray(productData.attributes) &&
                          productData.attributes.length > 0 && (
                            <div className="info-configuration">
                              {productData.attributes.map((attr, attrIndex) => (
                                <span key={attrIndex}>
                                  {attr.name}: {attr.value}
                                  <br />
                                </span>
                              ))}
                            </div>
                        )}

                        {/* SKU, Price, Qty all on one line */}
                        <div className="info-single-line">
                          <span className="info-inline-item">
                            <span className="label">SKU:</span>
                            <span className="value">{productData.SKU || 'N/A'}</span>
                          </span>
                          <span className="info-inline-item">
                            <span className="label">Price:</span>
                            <span className="value">{formatPrice(productData.price)}</span>
                          </span>
                          <span className="info-inline-item">
                            <span className="label">Qty:</span>
                            <span className="value">{product.quantity || 0}</span>
                          </span>
                          {productData['Specification-Weight'] && productData['Specification-Weight'] !== "N/A" && (
                            <span className="info-inline-item">
                              <span className="label">Spec Wt:</span>
                              <span className="value">{productData['Specification-Weight']}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {tracks.length > 0 && (
                      <div className="package-table-container">
                        <div className="package-table-header">
                          <span className="table-header-title">Package Details</span>
                        </div>

                        <div className="package-table-scroll">
                          <table className="package-table">
                            <thead>
                              <tr>
                                <th style={{width: '25%'}}>Package</th>
                                <th style={{width: '13%'}}>Actual Wt<br/>(Kg)</th>
                                <th style={{width: '13%'}}>Pkg Wt<br/>(Kg)</th>
                                <th style={{ width: '30%' }}>
                                  <div className="dim-header-cell">
                                    <span>Dimensions<br />(L×W×H) {selectedUnit}</span>
                                    <select
                                      value={selectedUnit}
                                      onChange={(e) => setSelectedUnit(e.target.value)}
                                      className="unit-select"
                                    >
                                      <option value="cm">cm</option>
                                      <option value="ft">ft</option>
                                    </select>
                                  </div>
                                </th>
                                <th style={{width: '19%'}}>Vol Wt<br/>(Kg)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tracks.map((track, trackIndex) => {
                                const actualWeight = getNumericWeight(track.actDeadWeight) || getNumericWeight(track.weight);
                                const packageWeight = getNumericWeight(track.pkgWeight) || getNumericWeight(track.weight);
                                const volumetricWeight = getNumericWeight(track.vWeight);

                                return (
                                  <tr key={trackIndex}>
                                    <td>{track.productName || `Package ${trackIndex + 1}`}</td>
                                    <td>{formatNumber(actualWeight)}</td>
                                    <td>{formatNumber(packageWeight)}</td>
                                    <td>{formatDimensions(track)}</td>
                                    <td>{formatNumber(volumetricWeight, '', true)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {shippingAddress && (
                <div className="address-section">
                  <h6 className="address-title">📍 Shipping Address</h6>
                  <div className="address-content">
                    <div className="address-name">
                      {shippingAddress.firstName} {shippingAddress.phoneNumber}
                    </div>
                    <div className="address-line">
                      {shippingAddress.address}
                    </div>
                    <div className="address-line">
                      {shippingAddress.city}, {shippingAddress.state} - {shippingAddress.zipcode}
                    </div>
                    {shippingAddress.landmark && (
                      <div className="address-line">
                        Landmark: {shippingAddress.landmark}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="package-report-section">
              <div className="package-report-header">
                <p>Please describe what's wrong with the product details shown above.</p>
              </div>
              
              <div className="package-textarea-container">
                <label className="package-textarea-label">
                  Describe the issue:
                </label>
                <textarea
                  className="package-report-textarea"
                  value={reportMessage}
                  onChange={(e) => setReportMessage(e.target.value)}
                  placeholder="Please describe what's wrong (e.g., wrong SKU, incorrect quantity, different product name, wrong weight, etc.)"
                  rows={4}
                />
              </div>
            </div>
          )}
        </div>

        {!showReportForm && !showSuccessMessage && shouldShowPackedCheckbox && (
          <div className="package-confirm">
            <label className="confirm-label">
              <input
                type="checkbox"
                checked={confirmAllPacked}
                onChange={(e) => setConfirmAllPacked(e.target.checked)}
                className="confirm-checkbox"
              />
              <span className="confirm-text">
                {/* SL-1144 */}
                I confirm that units (Qty: {totalPackages}) is/are packed.
                 {/* SL-1144 ends */}
              </span>
            </label>
            {/*!showReportForm && !showSuccessMessage && (
              <div className="package-penalty">
                Quantity mismatch or Dispatch errors will lead to penalty
              </div>
            )*/}
          </div>
        )}

        {!showSuccessMessage && (
          <div className="package-actions">
            {!showReportForm ? (
              <>
                <div className="confirmationSection">
                  <div className="confirmationText">Are you shipping the correct product(s) as shown above?</div>
                </div>
                <div className="action-buttons-group">
                  <button
                    className={`action-btn yes ${shouldShowPackedCheckbox && !confirmAllPacked ? 'disabled' : ''}`}
                    onClick={handleConfirm}
                    disabled={shouldShowPackedCheckbox && !confirmAllPacked}
                  >
                    ✓ Yes, Correct Product
                  </button>
                  <button className="action-btn no" onClick={handleCancel}>
                    ✗ No, Different Product
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="action-buttons-group" style={{marginLeft: 'auto'}}>
                  <button
                    className={`action-btn success ${!reportMessage.trim() || isSubmittingReport ? 'disabled' : ''}`}
                    onClick={handleReportSubmit}
                    disabled={!reportMessage.trim() || isSubmittingReport}
                  >
                    {isSubmittingReport ? "⏳ Submitting..." : "📤 Submit Report"}
                  </button>
                  <button
                    className="action-btn secondary"
                    onClick={handleReportCancel}
                    disabled={isSubmittingReport}
                  >
                    ↩️ Back to Confirmation
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default PackageDetailsPopup;